import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import { getClientBrowserInfo, getClientOSInfo, getClientIPAddress, validateEmail, containsEmoji } from '../../../utils/Utils';
import '../auth/Login.css';
import './CandidateRespondentForm.css';
import Header from '../layout/Header';
import { RESPONDENT_FORM_STEP, RELATIONSHIP_OPTIONS, getRelationshipString } from '../../../utils/Constants';
import { ApiError } from '../../../types/errors';

interface RespondentInfo {
  name: string;
  relationship: number;
  email: string;
}

interface FormErrors {
  respondent1?: {
    name?: string;
    relationship?: string;
    email?: string;
  };
  respondent2?: {
    name?: string;
    relationship?: string;
    email?: string;
  };
  general?: string;
  terms?: string;
}

const CandidateRespondentForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<number>(RESPONDENT_FORM_STEP.INPUT);
  const [candidateName, setCandidateName] = useState<string>('');
  const [candidateEmail, setCandidateEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);

  const [respondent1, setRespondent1] = useState<RespondentInfo>({
    name: '',
    relationship: 0,
    email: ''
  });

  const [respondent2, setRespondent2] = useState<RespondentInfo>({
    name: '',
    relationship: 0,
    email: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const fetchCandidateInfo = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await axios.get(`/candidates/verify-token/${token}`);

        if (response.data && response.data.candidate) {
          setCandidateName(response.data.candidate.name);
          setCandidateEmail(response.data.candidate.email);
        } else {
          throw new Error('候補者情報が見つかりませんでした');
        }

        setLoading(false);
      } catch (error: unknown) {
        console.error('Error fetching candidate info:', error);

        const axiosError = error as ApiError;

        if (axiosError.response && axiosError.response.status === 404) {
          navigate('/404');
          return;
        }

        if (axiosError.response && axiosError.response.status == 409) {
          // 既に登録済みの場合、完了画面を表示する。
          setCurrentStep(RESPONDENT_FORM_STEP.COMPLETED);
          setLoading(false);
          return;
        }

        setError('候補者情報の取得中にエラーが発生しました。URLを確認してください。');
        setLoading(false);
      }
    };

    if (token) {
      fetchCandidateInfo();
    } else {
      navigate('/404');
    }
  }, [token, navigate]);

  const handleRespondent1Change = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRespondent1(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRespondent1InputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRespondent1(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRespondent1SelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRespondent1(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRespondent2Change = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRespondent2(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRespondent2InputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRespondent2(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRespondent2SelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRespondent2(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isDuplicateMailAddress = (respondent1Email: string, respondent2Email: string): boolean => {
    respondent1Email.trim() && respondent2Email.trim();
    return respondent1Email.toLowerCase() === respondent2Email.toLowerCase();
  }

  const isSameWithCandidate = (respondentEmail: string): boolean => {
    respondentEmail.trim();
    return respondentEmail.toLowerCase() === candidateEmail.toLowerCase();
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!respondent1.name.trim()) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.name = '氏名を入力してください。';
      isValid = false;
    } else if (containsEmoji(respondent1.name)) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.name = '絵文字は使用できません。';
      isValid = false;
    }

    if (!respondent1.relationship) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.relationship = '関係性を選択してください';
      isValid = false;
    }

    if (!respondent1.email.trim()) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.email = 'メールアドレスを入力してください。';
      isValid = false;
    } else if (!validateEmail(respondent1.email)) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.email = '不正なメールアドレスです。';
      isValid = false;
    } else if (containsEmoji(respondent1.email)) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.email = '絵文字は使用できません。';
      isValid = false;
    } else if (isSameWithCandidate(respondent1.email)) {
      if (!newErrors.respondent1) newErrors.respondent1 = {};
      newErrors.respondent1.email = '候補者のメールアドレスです。';
      isValid = false;
    }

    if (!respondent2.name.trim()) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.name = '氏名を入力してください。';
      isValid = false;
    } else if (containsEmoji(respondent2.name)) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.name = '絵文字は使用できません。';
      isValid = false;
    }

    if (!respondent2.relationship) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.relationship = '関係性を選択してください';
      isValid = false;
    }

    if (!respondent2.email.trim()) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.email = 'メールアドレスを入力してください。';
      isValid = false;
    } else if (!validateEmail(respondent2.email)) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.email = '不正なメールアドレスです。';
      isValid = false;
    } else if (containsEmoji(respondent2.email)) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.email = '絵文字は使用できません。';
      isValid = false;
    } else if(isDuplicateMailAddress(respondent1.email, respondent2.email)) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.email = '1人目のメールアドレスとは異なるメールアドレスを設定してください。';
      isValid = false;
    } else if (isSameWithCandidate(respondent2.email)) {
      if (!newErrors.respondent2) newErrors.respondent2 = {};
      newErrors.respondent2.email = '候補者のメールアドレスです。';
      isValid = false;
    }

    if (!agreeToTerms) {
      newErrors.terms = '利用規約とプライバシーポリシーに同意してください';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleConfirm = (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (e.type === 'submit') {
      e.preventDefault();
    }

    if (validateForm()) {
      setCurrentStep(RESPONDENT_FORM_STEP.CONFIRM);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(RESPONDENT_FORM_STEP.INPUT);
    window.scrollTo(0, 0);
  };

  const handleSendData = async () => {
    setLoading(true);
    setError('');

    try {
      const browserInfo = getClientBrowserInfo();
      const osInfo = getClientOSInfo();
      const ipAddress = getClientIPAddress();

      const response = await axios.post('/respondents/store-multiple', {
        candidate_token: token,
        device_info: osInfo, 
        browser_info: browserInfo,
        ip_address: ipAddress,
        respondents: [
          {
            name: respondent1.name,
            email: respondent1.email,
            relationship: respondent1.relationship
          },
          {
            name: respondent2.name,
            email: respondent2.email,
            relationship: respondent2.relationship
          }
        ]
      });

      if (response.status === 201) {
        setCurrentStep(RESPONDENT_FORM_STEP.COMPLETED);
        setSuccess(true);

        localStorage.removeItem('respondent1');
        localStorage.removeItem('respondent2');
        localStorage.removeItem('candidateName');
        localStorage.removeItem('candidateToken');
      } else {
        throw new Error('APIレスポンスエラー');
      }

    } catch (error: unknown) {
      console.error('Error sending respondent emails:', error);
      const axiosError = error as ApiError;
      setError('回答者情報の送信中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">エラー発生</h4>

            <div className="text-center">
            <p>{error}</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (currentStep === RESPONDENT_FORM_STEP.COMPLETED) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">回答者情報を受け付けました</h4>

            <div className="text-center">
              <p>
                回答者情報のご入力ありがとうございます。<br />
                リファレンスチェック依頼を受け付けました。今後の進行をお待ちください。
              </p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (currentStep === RESPONDENT_FORM_STEP.CONFIRM) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />

        <Container>
          <h4 className="form-title">回答者情報入力画面</h4>
          <p>入力いただきますメールアドレスへリファレンスチェックの依頼URLをお送りいたします。</p>
          <div className="mb-5">
            <a href="/terms-of-service" className="legal-link" target="_blank" rel="noopener noreferrer">利用規約</a>
            と
            <a href="/privacy-policy" className="legal-link" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            に同意する。
          </div>

          <Row>
            <Col md={8}>
              {error && <Alert variant="danger">{error}</Alert>}

              <div className="form-container mb-4">
                <div className="form-header">
                  1人目：回答者情報
                </div>
                <div className="form-body">
                  <div className="respondent-section">
                    <div className="info-row">
                      <div className="info-label">氏名 <span className="text-danger">*</span></div>
                      <div className="info-value">{respondent1.name}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">関係性 <span className="text-danger">*</span></div>
                      <div className="info-value">{getRelationshipString(Number(respondent1.relationship))}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">メールアドレス <span className="text-danger">*</span></div>
                      <div className="info-value">{respondent1.email}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-container mb-4">
                <div className="form-header">
                  2人目：回答者情報
                </div>
                <div className="form-body">
                  <div className="respondent-section">
                    <div className="info-row">
                      <div className="info-label">氏名 <span className="text-danger">*</span></div>
                      <div className="info-value">{respondent2.name}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">関係性 <span className="text-danger">*</span></div>
                      <div className="info-value">{getRelationshipString(Number(respondent2.relationship))}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">メールアドレス <span className="text-danger">*</span></div>
                      <div className="info-value">{respondent2.email}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="confirmation-button-container">
                <Button 
                  variant="success" 
                  className="submit-button"
                  onClick={handleSendData}
                  disabled={loading}
                >
                  {loading ? '送信中...' : '送信'}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleBack}
                  className="back-button"
                  disabled={loading}
                >
                  戻る
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="respondent-form-container">
      <Header user={null} />

      <Container>
        <h4 className="form-title">回答者情報入力画面</h4>
        <p>入力いただきますメールアドレスへリファレンスチェックの依頼URLをお送りいたします。</p>
        <Form.Group className="mb-5">
          <Form.Check
            type="checkbox"
            id="agreeToTerms"
            label={
              <>
                <a href="/terms-of-service" className="legal-link" target="_blank" rel="noopener noreferrer">利用規約</a>
                と
                <a href="/privacy-policy" className="legal-link" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
                に同意する。
              </>
            }
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            isInvalid={!!errors.terms}
          />
          <Form.Control.Feedback type="invalid">
            {errors.terms}
          </Form.Control.Feedback>
        </Form.Group>

        <Row>
          <Col md={8}>

            {/* Respondent 1 */}
            <div className="form-container mb-4">
              <div className="form-header">
                1人目：回答者情報
              </div>
              <div className="form-body">
                <Form onSubmit={handleConfirm}>
                  <div className="respondent-section">
                    <Form.Group className="mb-3">
                      <Form.Label>氏名 <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={respondent1.name}
                        onChange={handleRespondent1InputChange}
                        placeholder="(例) 山田太郎"
                        isInvalid={!!errors.respondent1?.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent1?.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>関係性 <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="relationship"
                        value={respondent1.relationship}
                        onChange={handleRespondent1SelectChange}
                        isInvalid={!!errors.respondent1?.relationship}
                      >
                        <option value="">選択してください</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.text}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent1?.relationship}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>メールアドレス <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="email"
                        value={respondent1.email}
                        onChange={handleRespondent1InputChange}
                        placeholder="(例) xxxxxxxxxx@xxxxxxx.com"
                        isInvalid={!!errors.respondent1?.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent1?.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </Form>
              </div>
            </div>

            {/* Respondent 2 */}
            <div className="form-container mb-4">
              <div className="form-header">
                2人目：回答者情報
              </div>
              <div className="form-body">
                <Form>
                  <div className="respondent-section">
                    <Form.Group className="mb-3">
                      <Form.Label>氏名 <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={respondent2.name}
                        onChange={handleRespondent2InputChange}
                        placeholder="(例) 山田太郎"
                        isInvalid={!!errors.respondent2?.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent2?.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>関係性 <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="relationship"
                        value={respondent2.relationship}
                        onChange={handleRespondent2SelectChange}
                        isInvalid={!!errors.respondent2?.relationship}
                      >
                        <option value="">選択してください</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.text}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent2?.relationship}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>メールアドレス <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="email"
                        value={respondent2.email}
                        onChange={handleRespondent2InputChange}
                        placeholder="(例) xxxxxxxxxx@xxxxxxx.com"
                        isInvalid={!!errors.respondent2?.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.respondent2?.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="confirmation-button-container">
              <Button 
                variant="success" 
                className="submit-button"
                onClick={handleConfirm}
                disabled={loading}
              >
                確認画面へ
              </Button>
              <p className="note-text text-left">必須項目を入力してください</p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CandidateRespondentForm;
